import { fetchAndParseJson } from "../../../common-deprecated/utils";
import type { FetchAndParseJsonType } from "../../../common-deprecated/types/CommonTypes";
import type { CommonSettingsType } from "../../../common-deprecated/settings/fetchCommonSettings";
import type {
    CCGetMonthlyPaymentResponseType,
    ParsedMonthlyRateType,
    SaveAndPrintResponseType,
    CCGetMonthlyRatesResponseType,
    LegalTextType,
} from "../../../common-deprecated/types/FinanceInsuranceRateTypes";
import type { LegalTextResponseType } from "../types/FinanceTypes";
import type { ExternalConfigType } from "../types/ExternalPriceType";
import { formatCarFinance, processItemRates } from "./formatters/formatFinance";
import { sanitize } from "../../../common-deprecated/utils/sanitizer";

/**
 * TODO Typing in this function is fubar, fix this (Rewrite from scratch and include tests).
 */

/**
 * Get and the legal text property for finance and insurance objects.
 */
export const getLegalText = async (
    uri: string,
    // eslint-disable-next-line camelcase
    body: ExternalConfigType & { Legal_Text_ID: string; Execution_ID: string },
    loader: FetchAndParseJsonType,
): Promise<LegalTextType | null> => {
    const legalTextResponse: LegalTextResponseType = await loader(uri, "legal text", body);
    if (legalTextResponse) {
        const rawLegalText =
            legalTextResponse.Legal_Text.Promo_Legal_Text_Value || legalTextResponse.Legal_Text.Legal_Text_Value;
        const legalText: LegalTextType = {
            // If there is a promotion legal text available, take that one over the regular one
            value: rawLegalText ? sanitize(rawLegalText) : "",
        };
        if (legalTextResponse.Legal_Text.Calculations) {
            legalText.table = legalTextResponse.Legal_Text.Calculations.Calculation.map((item) => ({
                name: item.Calculation_Name,
                value: item.Calculation_Value ? sanitize(item.Calculation_Value) : "",
                tooltip: item.Tooltip ? sanitize(item.Tooltip) : "",
            }));
        }
        return legalText;
    }
    return null;
};

export const fetchMonthlyRate = async (
    settings: CommonSettingsType,
    componentSettings: {
        monthlyRateService: string;
        monthlyRateConfigurablesService: string;
        monthlyRateSelectedService: string;
        monthlyRateDisclaimerService: string;
    },
    config: ExternalConfigType,
    showAccessoriesRate?: boolean,
    selectedItems?: string[] | null,
    unSelectedItems?: string[] | null,
    fetchAndParseJsonOverwrite?: FetchAndParseJsonType,
): Promise<ParsedMonthlyRateType> => {
    const {
        monthlyRateService,
        monthlyRateConfigurablesService,
        monthlyRateSelectedService,
        monthlyRateDisclaimerService,
    } = componentSettings;
    let response: any = { info: {} };
    // RequestLoader is used server side, fetchAndParseJson is used client side
    const loader = fetchAndParseJsonOverwrite || fetchAndParseJson;
    const ratePromises = [loader(monthlyRateService, "monthly rate", config) as CCGetMonthlyPaymentResponseType | null];

    // Get rates for equipment if necessary.
    if (showAccessoriesRate) {
        const accRateConfig: any = {
            Brand: config.Brand,
            Country: settings.country,
            CarId: config.CarID,
            ExteriorColourId: config.ExteriorColourID,
            UpholsteryId: config.UpholsteryID,
            SelectedItems: selectedItems,
            UnselectedItems: unSelectedItems,
        };
        // Also inherit the personalized quote id parameters when relevant
        if (config.Parameters) {
            accRateConfig.Parameters = { ...config.Parameters };
        }
        // Also inherit PSLS information when applicable
        if (config.PSLS) {
            accRateConfig.PSLS = config.PSLS;
        }

        ratePromises.push(
            monthlyRateConfigurablesService
                ? loader(monthlyRateConfigurablesService, "unselected items rate", accRateConfig)
                : null,
        );

        ratePromises.push(
            monthlyRateSelectedService
                ? loader(monthlyRateSelectedService, "selected items rate", accRateConfig)
                : null,
        );
    }
    // Execute requests to monthly rate endpoints.
    const [carMonthlyRate, equipmentMonthlyRates, selectedEquipmentRates] = await Promise.all(ratePromises);
    const legalTextBody = {
        ...config,
        Legal_Text_ID: carMonthlyRate && carMonthlyRate.Quotation ? carMonthlyRate.Quotation.Legal_Text_ID : "",
        Execution_ID: carMonthlyRate && carMonthlyRate.Quotation ? carMonthlyRate.Quotation.Execution_ID : "",
    };

    if (carMonthlyRate) {
        const formattedCarFinance = formatCarFinance(carMonthlyRate);

        response = { ...response, ...formattedCarFinance };
    }

    response.unselectedItemPrices = equipmentMonthlyRates
        ? processItemRates<CCGetMonthlyRatesResponseType>(
              equipmentMonthlyRates as unknown as CCGetMonthlyRatesResponseType,
              "UnselectedItems",
          )
        : [];
    response.selectedItemPrices = selectedEquipmentRates
        ? processItemRates<SaveAndPrintResponseType>(
              selectedEquipmentRates as unknown as SaveAndPrintResponseType,
              "SelectedItems",
          )
        : [];
    // Do not load legal text if we don't have quotes to start with, this is also part of the response's info object
    // since this is being passed as is untill it's used in the (priceUtils) formatMonthlyPrice function.
    if (response.quoteInfo)
        response.info.disclaimer = await getLegalText(monthlyRateDisclaimerService, legalTextBody, loader);
    return response;
};
